import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { AlertTriangle, Loader2 } from 'lucide-react';
import type { ChoquesResponse, ClaseCompleta } from '../../services/api/plazaService';

/**
 * Aviso de clases llenas al dar una plaza.
 *
 * La disponibilidad de una plaza se cuenta por plazas, pero una clase concreta
 * puede estar llena por gente sin plaza en ese horario (alguien recuperando,
 * una clase de prueba…). Ahí el cliente nuevo no entra, porque nunca se pasa del
 * aforo. Este aviso lo enseña ANTES de confirmar, para poder explicárselo al
 * cliente, y deja decidir por cada clase si se le da una recuperación.
 *
 * La consulta se refresca cada 30 s (useChoquesPlazasQuery) y al guardar el
 * backend vuelve a comprobar con `choquesVistos` (idsChoques): si algo se llenó
 * desde el último refresco responde 409 y no guarda.
 *
 * El valor por defecto de la casilla lo calcula el backend: sin marcar si la
 * clase es anterior a su primera clase real (el prorrateo del primer mes empieza
 * después, no la paga), marcada si es posterior (sí la paga).
 */

export type Decisiones = Record<string, boolean>;

/** Decisiones iniciales: las que propone el backend para cada clase llena. */
export const decisionesPorDefecto = (choques?: ChoquesResponse): Decisiones =>
    Object.fromEntries(
        (choques?.plazas ?? []).flatMap((p) =>
            p.clasesCompletas.map((c) => [c.eventoId, c.compensarPorDefecto] as const)
        )
    );

export const hayChoques = (choques?: ChoquesResponse) =>
    !!choques?.plazas.some((p) => p.clasesCompletas.length > 0);

const plural = (n: number, singular: string, pluralForm: string) => `${n} ${n === 1 ? singular : pluralForm}`;

const describirMotivo = ({ motivo, aforo }: ClaseCompleta) => {
    const partes = [
        motivo.recuperaciones > 0 && plural(motivo.recuperaciones, 'persona recuperando', 'personas recuperando'),
        motivo.pruebas > 0 && plural(motivo.pruebas, 'clase de prueba', 'clases de prueba'),
        motivo.otros > 0 && plural(motivo.otros, 'persona añadida por el centro', 'personas añadidas por el centro'),
    ].filter(Boolean);
    return partes.length
        ? `Completa (${aforo}/${aforo}): ${partes.join(', ')}`
        : `Completa (${aforo}/${aforo})`;
};

/**
 * Frase del motivo para todo el aviso, con los tipos que aparecen de verdad:
 * "…completa con personas recuperando una clase o haciendo una clase de prueba".
 */
const describirOcupantes = (clases: ClaseCompleta[]) => {
    const hay = (k: keyof ClaseCompleta['motivo']) => clases.some((c) => c.motivo[k] > 0);
    const tipos = [
        hay('recuperaciones') && 'recuperando una clase',
        hay('pruebas') && 'haciendo una clase de prueba',
        hay('otros') && 'añadidas a esa clase por el centro',
    ].filter(Boolean) as string[];
    if (!tipos.length) return 'la clase ya está completa';
    const lista = tipos.length === 1
        ? tipos[0]
        : `${tipos.slice(0, -1).join(', ')} o ${tipos[tipos.length - 1]}`;
    return `la clase ya está completa con personas ${lista}`;
};

const fechaLarga = (iso: string) => format(new Date(iso), "EEEE d 'de' MMMM", { locale: es });

/** Ids de las clases llenas del aviso: lo que el operador ha visto. */
export const idsChoques = (choques?: ChoquesResponse) =>
    (choques?.plazas ?? []).flatMap((p) => p.clasesCompletas.map((c) => c.eventoId));

/**
 * Avisa con un toast cuando, al refrescarse, aparece una clase llena que antes
 * no estaba: el recuadro cambia solo y es fácil no verlo mientras se rellena el
 * formulario. La primera carga no avisa: para eso ya está el recuadro.
 */
const useAvisoNuevasCompletas = (choques?: ChoquesResponse) => {
    const vistas = useRef<{ horarios: string; ids: Set<string> } | null>(null);
    useEffect(() => {
        if (!choques) return;
        const actuales = choques.plazas.flatMap((p) => p.clasesCompletas);
        // Si cambian los horarios elegidos (otra hora en "añadir plaza") no es
        // que algo se haya llenado: se empieza de cero sin avisar.
        const horarios = choques.plazas.map((p) => `${p.nivel_id}-${p.dia}-${p.horaInicio}`).join('|');
        const anteriores = vistas.current?.horarios === horarios ? vistas.current.ids : null;
        vistas.current = { horarios, ids: new Set(actuales.map((c) => c.eventoId)) };
        if (!anteriores) return;

        const nuevas = actuales.filter((c) => !anteriores.has(c.eventoId));
        if (!nuevas.length) return;
        const fechas = nuevas.map((c) => fechaLarga(c.inicio)).join(', ');
        const primera = choques.primeraClaseCliente && new Date(choques.primeraClaseCliente) > new Date()
            ? ` Su primera clase pasa a ser el ${fechaLarga(choques.primeraClaseCliente)}.`
            : '';
        toast.warning(
            `${nuevas.length === 1 ? 'Se acaba de llenar la clase del' : 'Se acaban de llenar las clases del'} ${fechas}.${primera}`,
            { autoClose: 10000 }
        );
    }, [choques]);
};

interface Props {
    choques?: ChoquesResponse;
    isLoading: boolean;
    isError: boolean;
    decisiones: Decisiones;
    onChange: (eventoId: string, compensar: boolean) => void;
    nombreNivel?: (nivelId: string) => string | undefined;
    nombreCliente?: string;
    // Al MOVER una plaza no se compensa (fuera del alcance acordado): solo se avisa.
    soloAviso?: boolean;
}

export const ChoquesPlazasAviso = ({
    choques, isLoading, isError, decisiones, onChange, nombreNivel, nombreCliente, soloAviso = false,
}: Props) => {
    useAvisoNuevasCompletas(choques);

    if (isLoading) {
        return (
            <div className="flex items-center text-sm text-gray-500 my-3">
                <Loader2 className="animate-spin mr-2" size={16} />
                Comprobando las clases ya programadas…
            </div>
        );
    }

    if (isError) {
        return (
            <div className="my-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                No se pudo comprobar si alguna clase de estas plazas está completa. Si continúas,
                el cliente entrará en las que tengan sitio.
            </div>
        );
    }

    if (!hayChoques(choques)) return null;

    const quien = nombreCliente?.trim() || 'El cliente';
    const conChoques = choques!.plazas.filter((p) => p.clasesCompletas.length > 0);

    return (
        <div className="my-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
            <div className="flex items-start gap-2">
                <AlertTriangle className="text-amber-600 mt-0.5 shrink-0" size={20} />
                <div className="min-w-0">
                    <p className="font-semibold text-amber-900">
                        Hay clases completas en {conChoques.length === 1 ? 'esta plaza' : 'estas plazas'}
                    </p>
                    <p className="text-sm text-amber-800 mt-1">
                        La plaza está asegurada, pero en estas fechas {describirOcupantes(conChoques.flatMap((p) => p.clasesCompletas))}.
                        {' '}{quien} no podrá asistir esos días.
                        {/* Solo si aún no ha empezado: un cliente que ya viene tiene su primera clase en el pasado. */}
                        {choques!.primeraClaseCliente && new Date(choques!.primeraClaseCliente) > new Date() && (
                            <> Su primera clase será el <strong>{fechaLarga(choques!.primeraClaseCliente)}</strong>.</>
                        )}
                    </p>
                </div>
            </div>

            <div className="mt-3 space-y-3">
                {conChoques.map((p) => {
                    const nivel = nombreNivel?.(p.nivel_id);
                    return (
                        <div key={`${p.nivel_id}-${p.dia}-${p.horaInicio}`} className="rounded-md bg-white border border-amber-200 px-3 py-2">
                            <p className="font-medium text-gray-900">
                                {p.dia} {p.horaInicio}{nivel ? ` · ${nivel}` : ''}
                            </p>
                            <p className="text-xs text-gray-500">
                                {p.primeraClase
                                    ? `En este horario empieza el ${fechaLarga(p.primeraClase)}.`
                                    : 'No queda ninguna clase con sitio en este horario.'}
                            </p>
                            <ul className="mt-2 space-y-2">
                                {p.clasesCompletas.map((c) => {
                                    const id = `compensar-${c.eventoId}`;
                                    return (
                                        <li key={c.eventoId} className="text-sm">
                                            <p className="text-gray-800">
                                                <span className="font-medium">{fechaLarga(c.inicio)}</span>
                                                {' — '}
                                                <span className="text-gray-600">{describirMotivo(c)}</span>
                                            </p>
                                            {!soloAviso && (
                                                <label htmlFor={id} className="mt-1 flex items-center gap-2 text-gray-700 cursor-pointer">
                                                    <input
                                                        id={id}
                                                        type="checkbox"
                                                        checked={decisiones[c.eventoId] ?? c.compensarPorDefecto}
                                                        onChange={(e) => onChange(c.eventoId, e.target.checked)}
                                                        className="h-4 w-4 rounded border-gray-300"
                                                    />
                                                    Dar una recuperación por esta clase
                                                </label>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    );
                })}
            </div>

            {soloAviso ? (
                <p className="text-xs text-amber-800 mt-3">
                    Al mover la plaza no se dan recuperaciones por estas clases. Si hace falta, concédela a
                    mano desde la ficha del cliente.
                </p>
            ) : (
                <p className="text-xs text-amber-800 mt-3">
                    Marca la recuperación si esa clase entra en lo que se le cobra. Viene marcada cuando la
                    clase es posterior a su primera clase; sin marcar cuando es anterior, porque el primer
                    mes se prorratea desde la primera clase a la que puede ir.
                </p>
            )}
        </div>
    );
};

export default ChoquesPlazasAviso;
